<template>
  <div class="prompts-management">
    <div class="page-header">
      <div class="header-content">
        <h1>{{ $t('prompts.title') }}</h1>
        <p class="header-subtitle">{{ $t('prompts.subtitle') }}</p>
      </div>
      <button v-if="prompts.length > 0 && !showAddForm && !editingPrompt" @click="showAddForm = true" class="btn btn-primary">
        {{ $t('prompts.create') }}
      </button>
    </div>

    <!-- Форма добавления/редактирования промпта -->
    <transition name="slide-fade">
      <div v-if="showAddForm || editingPrompt" class="prompt-form">
        <div class="form-header">
          <h3>{{ editingPrompt ? $t('prompts.editPrompt') : $t('prompts.newPrompt') }}</h3>
          <button @click="cancelForm" class="btn-close" :title="$t('common.close')">×</button>
        </div>
        <div class="form-body">
          <div class="form-group">
            <label for="prompt-name">{{ $t('prompts.name') }}</label>
            <input 
              id="prompt-name"
              v-model="formData.name" 
              type="text" 
              class="form-input" 
              :placeholder="$t('prompts.namePlaceholder')"
              maxlength="100"
            />
          </div>
          <div class="form-group">
            <label for="prompt-content">{{ $t('prompts.content') }}</label>
            <textarea 
              id="prompt-content"
              v-model="formData.content" 
              class="form-textarea" 
              :placeholder="$t('prompts.contentPlaceholder')"
              rows="8"
              maxlength="5000"
            ></textarea>
            <div class="char-counter">{{ formData.content.length }} / 5000</div>
          </div>
          <div class="form-actions">
            <button @click="cancelForm" class="btn btn-secondary">{{ $t('prompts.cancel') }}</button>
            <button @click="savePrompt" class="btn btn-primary" :disabled="saving || !isFormValid">
              {{ saving ? $t('prompts.saving') : (editingPrompt ? $t('prompts.save') : $t('prompts.create')) }}
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- Сообщения -->
    <transition name="fade">
      <div v-if="message" :class="['notification', messageType]">
        {{ message }}
      </div>
    </transition>

    <!-- Список промптов -->
    <div class="prompts-container">
      <!-- Загрузка -->
      <div v-if="loading" class="loading-state">
        <div class="spinner"></div>
        {{ $t('prompts.loading') }}
      </div>

      <!-- Пустое состояние -->
      <div v-else-if="prompts.length === 0 && !showAddForm && !editingPrompt" class="empty-state">
        <button @click="showAddForm = true" class="btn btn-primary">
          {{ $t('prompts.createFirst') }}
        </button>
      </div>

      <!-- Список промптов -->
      <div v-else class="prompts-list">
        <div v-for="prompt in prompts" :key="prompt.id" class="prompt-card">
          <div class="prompt-header">
            <h3 class="prompt-name">{{ prompt.name }}</h3>
            <div class="prompt-actions">
              <button @click="startEdit(prompt)" class="btn-icon" :title="t('common.edit')">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                  <path d="M11.013 1.427a1.75 1.75 0 0 1 2.474 0l1.086 1.086a1.75 1.75 0 0 1 0 2.474l-8.61 8.61c-.21.21-.47.364-.756.445l-3.251.93a.75.75 0 0 1-.927-.928l.929-3.25c.081-.286.235-.547.445-.758l8.61-8.61Zm.176 4.823L9.75 4.81l-6.286 6.287a.253.253 0 0 0-.064.108l-.558 1.953 1.953-.558a.253.253 0 0 0 .108-.064l6.286-6.286Z"/>
                </svg>
              </button>
              <button @click="confirmDelete(prompt.id)" class="btn-icon btn-delete" :title="t('common.delete')">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                  <path d="M11 1.75V3h2.25a.75.75 0 0 1 0 1.5H2.75a.75.75 0 0 1 0-1.5H5V1.75C5 .784 5.784 0 6.75 0h2.5C10.216 0 11 .784 11 1.75zM4.496 6.675l.66 6.6a.25.25 0 0 0 .249.225h5.19a.25.25 0 0 0 .249-.225l.66-6.6a.75.75 0 0 1 1.492.149l-.66 6.6A1.748 1.748 0 0 1 10.595 15h-5.19a1.748 1.748 0 0 1-1.741-1.575l-.66-6.6a.75.75 0 1 1 1.492-.15zM6.5 1.75V3h3V1.75a.25.25 0 0 0-.25-.25h-2.5a.25.25 0 0 0-.25.25z"/>
                </svg>
              </button>
            </div>
          </div>
          <div class="prompt-content">
            {{ prompt.content }}
          </div>
          <div class="prompt-footer">
            <span class="prompt-date">
              {{ t('prompts.createdAt') }}: {{ formatDate(prompt.created_at) }}
            </span>
            <span v-if="prompt.updated_at !== prompt.created_at" class="prompt-date">
              {{ t('prompts.updatedAt') }}: {{ formatDate(prompt.updated_at) }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Диалог подтверждения удаления -->
    <transition name="modal-fade">
      <div v-if="deleteConfirmId" class="modal-overlay" @click="cancelDelete">
        <div class="delete-confirm-dialog" @click.stop>
          <div class="delete-confirm-header">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
              <line x1="12" y1="9" x2="12" y2="13"/>
              <line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
            <h3>{{ t('prompts.confirmDeleteTitle') }}</h3>
          </div>
          <p class="delete-confirm-message">
            {{ t('prompts.confirmDeleteMessage') }}
          </p>
          <p class="delete-confirm-warning">
            {{ t('prompts.confirmDeleteWarning') }}
          </p>
          <div class="delete-confirm-actions">
            <button @click="cancelDelete" class="btn btn-secondary">{{ t('prompts.deleteCancel') }}</button>
            <button @click="deletePrompt" class="btn btn-danger" :disabled="deleting">
              {{ deleting ? t('prompts.deleting') : t('prompts.deleteConfirm') }}
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { authService } from '../services/auth'
import { useLanguage } from '../composables/useLanguage'

export default {
  name: 'PromptsManagement',
  setup() {
    const { t, locale } = useLanguage()
    const prompts = ref([])
    const loading = ref(false)
    const saving = ref(false)
    const deleting = ref(false)
    const showAddForm = ref(false)
    const editingPrompt = ref(null)
    const deleteConfirmId = ref(null)
    const message = ref('')
    const messageType = ref('')
    
    const formData = ref({
      name: '',
      content: ''
    })

    const isFormValid = computed(() => {
      return formData.value.name.trim() && formData.value.content.trim()
    })

    const apiRequest = async (url, options = {}) => {
      const token = await authService.getToken()  // ИСПРАВЛЕНО: добавлен await для async функции
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }

      const response = await fetch(`${import.meta.env.VITE_API_URL || ''}${url}`, {
        ...options,
        headers: { ...headers, ...options.headers }
      })

      if (response.status === 401) {
        authService.logout()
        window.location.href = '/'
        throw new Error('Unauthorized')
      }

      return response
    }

    const loadPrompts = async () => {
      loading.value = true
      try {
        const response = await apiRequest('/user/prompts')
        if (response.ok) {
          const data = await response.json()
          prompts.value = data.prompts || []
        } else {
          showMessage(t('prompts.loadError'), 'error')
        }
      } catch (error) {
        console.error('Error loading prompts:', error)
        showMessage(t('common.connectionError'), 'error')
      } finally {
        loading.value = false
      }
    }

    const savePrompt = async () => {
      if (!isFormValid.value) return

      saving.value = true
      try {
        const url = editingPrompt.value 
          ? `/user/prompts/${editingPrompt.value.id}` 
          : '/user/prompts'
        
        const method = editingPrompt.value ? 'PUT' : 'POST'

        const response = await apiRequest(url, {
          method,
          body: JSON.stringify({
            name: formData.value.name.trim(),
            content: formData.value.content.trim()
          })
        })

        if (response.ok) {
          showMessage(
            editingPrompt.value ? t('prompts.promptUpdated') : t('prompts.promptCreated'),
            'success'
          )
          await loadPrompts()
          cancelForm()
        } else {
          const errorData = await response.json()
          showMessage(errorData.detail || t('prompts.saveError'), 'error')
        }
      } catch (error) {
        console.error('Error saving prompt:', error)
        showMessage(t('common.connectionError'), 'error')
      } finally {
        saving.value = false
      }
    }

    const startEdit = (prompt) => {
      editingPrompt.value = prompt
      formData.value = {
        name: prompt.name,
        content: prompt.content
      }
      showAddForm.value = false
    }

    const confirmDelete = (promptId) => {
      deleteConfirmId.value = promptId
    }

    const cancelDelete = () => {
      deleteConfirmId.value = null
    }

    const deletePrompt = async () => {
      if (!deleteConfirmId.value) return

      deleting.value = true
      try {
        const response = await apiRequest(`/user/prompts/${deleteConfirmId.value}`, {
          method: 'DELETE'
        })

        if (response.ok) {
          showMessage(t('prompts.promptDeleted'), 'success')
          await loadPrompts()
          deleteConfirmId.value = null
        } else {
          const errorData = await response.json()
          showMessage(errorData.detail || t('prompts.deleteError'), 'error')
        }
      } catch (error) {
        console.error('Error deleting prompt:', error)
        showMessage(t('common.connectionError'), 'error')
      } finally {
        deleting.value = false
      }
    }

    const cancelForm = () => {
      showAddForm.value = false
      editingPrompt.value = null
      formData.value = {
        name: '',
        content: ''
      }
    }

    const showMessage = (text, type) => {
      message.value = text
      messageType.value = type
      setTimeout(() => {
        message.value = ''
      }, 3000)
    }

    const formatDate = (timestamp) => {
      const date = new Date(timestamp * 1000)
      return date.toLocaleString(locale.value, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }

    onMounted(() => {
      loadPrompts()
    })

    return {
      t,
      prompts,
      loading,
      saving,
      deleting,
      showAddForm,
      editingPrompt,
      deleteConfirmId,
      message,
      messageType,
      formData,
      isFormValid,
      savePrompt,
      startEdit,
      confirmDelete,
      cancelDelete,
      deletePrompt,
      cancelForm,
      formatDate
    }
  }
}
</script>

<style scoped>
.prompts-management {
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;
  gap: 2rem;
}

.header-content h1 {
  margin: 0 0 0.5rem 0;
  font-size: 2rem;
  font-weight: 700;
  color: var(--text-primary);
  padding-bottom: 1rem;
  border-bottom: 3px solid var(--primary-color);
}

.header-subtitle {
  margin: 0;
  color: var(--text-secondary);
  font-size: 0.95rem;
}

/* Форма */
.prompt-form {
  background: var(--bg-secondary);
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 2rem;
  border: 1px solid var(--border-color);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.form-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.form-header h3 {
  margin: 0;
  font-size: 1.25rem;
  color: var(--text-primary);
}

.btn-close {
  background: none;
  border: none;
  font-size: 2rem;
  line-height: 1;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: all 0.2s;
}

.btn-close:hover {
  background: var(--bg-tertiary);
  color: var(--text-primary);
}

.form-body {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-group label {
  font-weight: 600;
  color: var(--text-primary);
  font-size: 0.9rem;
}

.form-input,
.form-textarea {
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  background: var(--bg-primary);
  color: var(--text-primary);
  font-family: 'JetBrains Mono', monospace, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
  font-size: 0.9rem;
  transition: all 0.2s;
}

.form-input:focus,
.form-textarea:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px rgba(76, 175, 80, 0.1);
}

.form-textarea {
  resize: vertical;
  min-height: 150px;
}

.char-counter {
  text-align: right;
  font-size: 0.85rem;
  color: var(--text-secondary);
}

.form-actions {
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  margin-top: 0.5rem;
}

/* Список промптов */
.prompts-container {
  min-height: 300px;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  color: var(--text-secondary);
}

.spinner {
  border: 3px solid var(--bg-tertiary);
  border-top-color: var(--primary-color);
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.empty-state {
  text-align: center;
  padding: 4rem 2rem;
  color: var(--text-secondary);
}

.empty-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
  opacity: 0.4;
}

.empty-message {
  margin: 0 0 1.5rem 0;
  font-size: 1rem;
  color: var(--text-secondary);
}

.empty-state p {
  margin: 0 0 1.5rem 0;
  font-size: 1.1rem;
}

.prompts-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 1.5rem;
}

.prompt-card {
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  padding: 1.5rem;
  transition: all 0.2s;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.prompt-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  border-color: var(--primary-color);
}

.prompt-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
}

.prompt-name {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--text-primary);
  flex: 1;
}

.prompt-actions {
  display: flex;
  gap: 0.5rem;
}

.btn-icon {
  background: var(--bg-tertiary);
  border: 1px solid var(--border-color);
  border-radius: 6px;
  padding: 0.5rem;
  cursor: pointer;
  color: var(--text-secondary);
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-icon:hover {
  background: var(--primary-color);
  border-color: var(--primary-color);
  color: white;
  transform: translateY(-1px);
}

.btn-icon.btn-delete:hover {
  background: var(--error-color);
  border-color: var(--error-color);
}

.prompt-content {
  color: var(--text-secondary);
  line-height: 1.6;
  font-size: 0.9rem;
  white-space: pre-wrap;
  word-break: break-word;
  flex: 1;
  max-height: 200px;
  overflow-y: auto;
  padding: 0.75rem;
  background: var(--bg-primary);
  border-radius: 6px;
  border: 1px solid var(--border-color);
}

.prompt-footer {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  padding-top: 0.75rem;
  border-top: 1px solid var(--border-color);
}

.prompt-date {
  font-size: 0.8rem;
  color: var(--text-tertiary);
}

/* Кнопки */
.btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  font-family: 'JetBrains Mono', monospace, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
}

.btn-primary {
  background: var(--primary-color);
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: var(--success-color);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-secondary {
  background: var(--bg-tertiary);
  color: var(--text-primary);
  border: 1px solid var(--border-color);
}

.btn-secondary:hover {
  background: var(--bg-secondary);
  border-color: var(--primary-color);
}

.btn-danger {
  background: var(--error-color);
  color: white;
}

.btn-danger:hover:not(:disabled) {
  background: #c62828;
  transform: translateY(-1px);
}

.btn-danger:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Уведомления */
.notification {
  position: fixed;
  top: 5rem;
  right: 2rem;
  padding: 1rem 1.5rem;
  border-radius: 8px;
  font-weight: 500;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 10000;
  max-width: 400px;
}

.notification.success {
  background: var(--success-bg);
  color: var(--success-color);
  border: 1px solid var(--success-color);
}

.notification.error {
  background: var(--error-bg);
  color: var(--error-color);
  border: 1px solid var(--error-color);
}

/* Диалог подтверждения */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
}

.delete-confirm-dialog {
  background: var(--bg-primary);
  border-radius: 12px;
  padding: 2rem;
  max-width: 450px;
  width: 90%;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  border: 1px solid var(--border-color);
}

.delete-confirm-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
  color: var(--error-color);
}

.delete-confirm-header svg {
  flex-shrink: 0;
}

.delete-confirm-header h3 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-primary);
}

.delete-confirm-message {
  margin: 0 0 1rem 0;
  color: var(--text-secondary);
  line-height: 1.8;
  text-align: center;
  font-size: 0.95rem;
  padding: 0 0.5rem;
}

.delete-confirm-warning {
  margin: 0 0 1.5rem 0;
  color: var(--error-color);
  line-height: 1.8;
  text-align: center;
  font-size: 0.9rem;
  font-weight: 500;
  padding: 0 0.5rem;
}

.delete-confirm-actions {
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
}

/* Анимации */
.slide-fade-enter-active, .slide-fade-leave-active {
  transition: all 0.3s ease;
}

.slide-fade-enter-from {
  transform: translateY(-20px);
  opacity: 0;
}

.slide-fade-leave-to {
  transform: translateY(-20px);
  opacity: 0;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

.modal-fade-enter-active, .modal-fade-leave-active {
  transition: opacity 0.2s ease;
}

.modal-fade-enter-from, .modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter-active .delete-confirm-dialog {
  animation: dialogSlideIn 0.3s ease;
}

@keyframes dialogSlideIn {
  from {
    transform: scale(0.9) translateY(-20px);
    opacity: 0;
  }
  to {
    transform: scale(1) translateY(0);
    opacity: 1;
  }
}

/* Адаптивность */
@media (max-width: 768px) {
  .prompts-management {
    padding: 1rem;
  }

  .page-header {
    flex-direction: column;
    align-items: stretch;
  }

  .btn-primary {
    width: 100%;
    justify-content: center;
  }

  .prompts-list {
    grid-template-columns: 1fr;
  }

  .notification {
    right: 1rem;
    left: 1rem;
    max-width: none;
  }
}
</style>



